import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// @MUI Imports
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';

// @Icons Imports
import Close from 'mdi-material-ui/Close';

// @Models
import { IBudgetById } from '../../../../models';

// @Utils
import { axiosEstimates } from '../../../../utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/reducers';

interface Prop {
  open: boolean;
  toggle(): void;
  idBudget: number;
}

type Budget = Omit<IBudgetById, 'customer'>;

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(3, 4),
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.paper,
}));

export function RequestContent({ open, toggle, idBudget }: Prop) {
  const { t, i18n } = useTranslation('common');
  const [loading, setLoading] = useState<boolean>(false);
  const [budget, setBudget] = useState<Budget | undefined>();
  const {
    countries: { countries },
  } = useSelector((state: RootState) => state);

  const getCountryName = (code: string) => {
    const country = countries.find(country => country.alpha2Code === code);

    if (!country) return code;

    return i18n.language.startsWith('en') ? country.nameEn : country.name;
  };

  const getRequest = async () => {
    setLoading(true);
    try {
      const response = await axiosEstimates.get<Budget>('supplier-chat-get-request/' + idBudget);
      setBudget(response.data);
      setLoading(false);
    } catch (error) {
      setBudget(undefined);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      getRequest();
    }
  }, [open]);

  return (
    <Drawer
      open={open}
      anchor='right'
      variant='temporary'
      onClose={toggle}
      ModalProps={{ keepMounted: true }}
      sx={{ '& .MuiDrawer-paper': { width: { xs: 300, sm: 400, md: 500 } } }}
    >
      <Header>
        <Typography variant='h6'>{t('supplierRequestContent.title')}</Typography>
        <Close fontSize='small' onClick={toggle} sx={{ cursor: 'pointer' }} />
      </Header>
      <Box sx={{ padding: '1rem' }}>
        {!loading && budget ? (
          <Box>
            <Box>
              <Typography variant='h5' sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                {t('supplierRequestContent.greeting')}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('supplierRequestContent.intro')}</Typography>
            </Box>
            <Divider sx={{ margin: '0.5rem 0' }} />
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                {t('supplierRequestContent.totalServices', { count: budget.shipments.length })}
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                {t('supplierRequestContent.incoterms', { value: budget.budget.incoterms })}
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                {t('supplierRequestContent.origin', {
                  country: getCountryName(budget.shipments[0].origin),
                  address: budget.shipments[0].addressOrigin,
                })}
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                {t('supplierRequestContent.destination', {
                  country: getCountryName(budget.shipments[0].destiny),
                  address: budget.shipments[0].addressDestiny,
                })}
              </Typography>
            </Box>
            <Box>
              {budget.shipments.map((shipment, index) => (
                <Box key={index} sx={{ padding: '0.5rem' }}>
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    {t('supplierRequestContent.serviceTitle', { index: index + 1 })}
                  </Typography>

                  <Divider sx={{ marginBottom: '0.5rem' }} />
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    {t('supplierRequestContent.serviceDescription', {
                      content: shipment.content,
                      dimensionUnit: shipment.unitOfMeasurement === 'cmkg' ? 'CM' : 'IN',
                      length: shipment.length,
                      high: shipment.high,
                      width: shipment.width,
                      physicalWeight: shipment.physicalWeight,
                      weightUnit: shipment.unitOfMeasurement === 'cmkg' ? 'KG' : 'LB',
                      volumeLabel:
                        shipment.unitOfMeasurement === 'cmkg' ? 'CBM' : t('supplierRequestContent.cubicFeet'),
                      area: shipment.area,
                      volumeUnit: shipment.unitOfMeasurement === 'cmkg' ? 'M3' : 'Ft3',
                    })}
                  </Typography>
                  {shipment.insurance !== 0 && (
                    <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                      {t('supplierRequestContent.invoiceValue', { value: shipment.insurance })}
                    </Typography>
                  )}
                  {shipment.comments !== '' && (
                    <>
                      <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                        {t('supplierRequestContent.comments')}
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                        {shipment.comments}
                      </Typography>
                    </>
                  )}
                </Box>
              ))}
            </Box>
            <br />

            <Box>
              <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                {t('supplierRequestContent.thanks')}
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                {t('supplierRequestContent.waiting')}
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                {t('supplierRequestContent.regards')}
              </Typography>
              <br />
            </Box>
          </Box>
        ) : (
          <Stack spacing={1}>
            <Skeleton variant='text' sx={{ fontSize: '1rem' }} />
            <Skeleton variant='rounded' width={'100%'} height={40} />
            <Skeleton variant='rectangular' width={'100%'} height={40} />
            <Skeleton variant='rounded' width={'100%'} height={40} />
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
